import { useState, useEffect, useCallback } from 'react';
import { Download, Users, Ban, CheckCircle, CreditCard, Loader2, BarChart3, FileText, Filter } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  const [funnel, setFunnel] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(() => {
    Promise.all([
      fetch(`${API}/api/admin/funnel`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/api/admin/cohorts`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([f, c]) => { setFunnel(f.funnel || []); setCohorts(c.cohorts || []); })
      .catch(() => { /* non-critical */ }).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = async (type) => {
    try {
      const res = await fetch(`${API}/api/admin/export/${type}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sendpi_${type}_export.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${type} exported!`);
    } catch (e) { toast.error('Export failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-white/30 animate-spin" /></div>;

  return (
    <div className="space-y-8" data-testid="admin-analytics">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white font-space">Deep Analytics</h1>
          <p className="text-white/40 mt-1">Cohorts, funnels, and exports</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => exportCSV('users')}
            className="border-white/10 text-white/60 hover:text-white text-xs" data-testid="export-users">
            <Download className="w-3 h-3 mr-1" /> Users CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportCSV('metrics')}
            className="border-white/10 text-white/60 hover:text-white text-xs" data-testid="export-metrics">
            <Download className="w-3 h-3 mr-1" /> Metrics CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportCSV('audit')}
            className="border-white/10 text-white/60 hover:text-white text-xs" data-testid="export-audit">
            <Download className="w-3 h-3 mr-1" /> Audit CSV
          </Button>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="funnel-chart">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <Filter className="w-5 h-5 text-cyan-400" /> Conversion Funnel
        </h2>
        <div className="space-y-3">
          {funnel.map((step, i) => {
            const width = Math.max(step.rate, 8);
            const colors = ['from-cyan-500 to-cyan-400', 'from-blue-500 to-blue-400', 'from-indigo-500 to-indigo-400', 'from-purple-500 to-purple-400', 'from-pink-500 to-pink-400', 'from-amber-500 to-amber-400', 'from-emerald-500 to-emerald-400'];
            return (
              <div key={step.stage} className="flex items-center gap-4">
                <span className="text-white/50 text-xs w-36 text-right shrink-0">{step.stage}</span>
                <div className="flex-1 relative">
                  <div className={`h-8 rounded-lg bg-gradient-to-r ${colors[i % colors.length]} flex items-center px-3 transition-all`}
                    style={{ width: `${width}%` }}>
                    <span className="text-white text-xs font-bold whitespace-nowrap">{step.count}</span>
                  </div>
                </div>
                <span className="text-white/40 text-xs w-12 text-right">{step.rate}%</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Cohort Analysis */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6" data-testid="cohort-table">
        <h2 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-purple-400" /> Weekly Cohort Retention
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-3 py-2 text-white/40">Cohort Week</th>
                <th className="text-center px-3 py-2 text-white/40">Size</th>
                <th className="text-center px-3 py-2 text-white/40">Week 0</th>
                <th className="text-center px-3 py-2 text-white/40">Week 1</th>
                <th className="text-center px-3 py-2 text-white/40">Week 2</th>
                <th className="text-center px-3 py-2 text-white/40">Week 3</th>
                <th className="text-center px-3 py-2 text-white/40">Week 4</th>
              </tr>
            </thead>
            <tbody>
              {cohorts.map(c => (
                <tr key={c.week} className="border-b border-white/5">
                  <td className="px-3 py-2 text-white/70">{c.week}</td>
                  <td className="px-3 py-2 text-center text-white font-medium">{c.size}</td>
                  {[0, 1, 2, 3, 4].map(w => {
                    const val = c.retention?.[w];
                    const bg = val === undefined ? '' :
                      val >= 50 ? 'bg-emerald-500/30' :
                      val >= 25 ? 'bg-amber-500/30' :
                      val > 0 ? 'bg-red-500/20' : '';
                    return (
                      <td key={w} className={`px-3 py-2 text-center text-white/60 ${bg} rounded`}>
                        {val !== undefined ? `${val}%` : '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {cohorts.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-white/30">No cohort data yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
